import type { QueryClient } from "@tanstack/react-query";

import type { HubCacheSseEvent } from "@/lib/hubCacheChannels";
import { HUB_CACHE_CHANNEL } from "@/lib/hubCacheChannels";
import {
  fetchNotificationsUnreadCount,
  notificationsUnreadCountQueryKey,
} from "@/lib/notificationsApi";

/** Live updates come from the presence tab stream connected snapshot; no boot GET. */
export const notificationsUnreadCountLiveQueryOptions = {
  queryKey: notificationsUnreadCountQueryKey,
  queryFn: fetchNotificationsUnreadCount,
  refetchInterval: false as const,
  refetchIntervalInBackground: false as const,
  refetchOnWindowFocus: false as const,
  refetchOnReconnect: false as const,
  staleTime: Infinity,
};

export function isHubNotificationsUnreadSseEvent(
  event: HubCacheSseEvent | { phase: "connected"; tabId?: string; channels?: string[] },
): event is HubCacheSseEvent & { unread_count: number } {
  if (!("channel" in event) || typeof event.channel !== "string") {
    return false;
  }
  if (event.phase === "connected") {
    return false;
  }
  if (typeof event.unread_count !== "number" || !Number.isFinite(event.unread_count)) {
    return false;
  }
  return event.channel.startsWith(`${HUB_CACHE_CHANNEL.hubNotificationsUnread("")}`);
}

export function applyHubNotificationsUnreadSseEvent(
  queryClient: QueryClient,
  event: HubCacheSseEvent | { phase: "connected"; tabId?: string; channels?: string[] },
): boolean {
  if (!isHubNotificationsUnreadSseEvent(event)) {
    return false;
  }
  const surfaceEventIds = Array.isArray(event.surface_event_ids)
    ? event.surface_event_ids.filter((eventId) => typeof eventId === "string" && eventId.trim() !== "")
    : [];
  queryClient.setQueryData(notificationsUnreadCountQueryKey, {
    unreadCount: Math.max(0, Math.floor(event.unread_count)),
    ...(surfaceEventIds.length > 0
      ? { surfaceEventIds, arrivalToken: Date.now() }
      : {}),
  });
  void queryClient.invalidateQueries({
    queryKey: ["notifications", "list"],
    refetchType: "active",
  });
  return true;
}
